/* eslint-disable camelcase */
import React from 'react';
import WholeMeProfileSectionDetail from 'pages/wholeme/molecules/wholeMeProfileSectionDetail/WholeMeProfileSectionDetail';
import PillRenderer from 'common/molecules/pillRenderer/PillRenderer';
import { InterestsIcon } from 'pages/wholeme/icons/Icons';
import WholeMeProfileSection from '../wholeMeProfileSection/WholeMeProfileSection';
import WholeMeProfileSectionRow from '../wholeMeProfileSectionRow/WholeMeProfileSectionRow';
import { fieldInfoUrlFor, fieldLabelFor } from '../../util/profile_field_data';
import {
  pillOverrideStyles,
  pillRendererOverrideStyles,
} from '../wholeMeProfileSection/wholeMeProfileSectionStyles';
import {
  resolveReligionValue,
  mapQuizValues,
  resolveQuizIcon,
} from '../wholeMeProfileIdentity/utils';
import { resolveDefaultIcons, firstValueOf } from '../../util/profile_util';

const RenderEditForOldIdentityFields = ({
  onClick, text, category, step, isCreate, items,
}) => (
  <div style={{ marginBottom: '12px' }}>
    <WholeMeProfileSectionRow
      styleOverrides={{ paddingBottom: '0' }}
      label={text}
      onEdit={onClick}
      navigationMeta={{ categoryId: category, stepId: step }}
      isCreate={isCreate}
      labelStyle={{ fontWeight: 700, lineHeight: 'normal', color: '#063D8F' }}
    />
    <PillRenderer
      items={items || []}
      pillStyles={pillOverrideStyles}
      containerStyleOverrides={{
        ...pillRendererOverrideStyles,
        marginLeft: '22px',
      }}
    />
  </div>
);

const WholeMeProfileInterests = ({
  profile, onEdit, isViewOnly,
}) => {
  const pillContainerOverrideStyles = isViewOnly
    ? pillRendererOverrideStyles
    : {
      ...pillRendererOverrideStyles,
      marginLeft: '22px',
    };
  const {
    hobbies = [], clubs = [], religion = [], quiz = [],
  } = profile;
  const isViewingAdmin = (firstValueOf(profile?.role) || profile?.publicProfileData?.role) === 'admin';
  const quizValues = mapQuizValues(quiz);

  if (!onEdit && hobbies.length < 1 && clubs.length < 1 && religion.length < 1) {
    return null;
  }

  const renderEdit = () => (
    <>
      {Object.keys(quizValues).map((key) => (
        <RenderEditForOldIdentityFields
          key={key}
          onClick={onEdit}
          text={key}
          category="identity"
          step="quiz"
          isCreate={false}
          items={quizValues[key]}
        />
      ))}
      <RenderEditForOldIdentityFields
        onClick={onEdit}
        text="Religion"
        category="identity"
        step="religion"
        isCreate={false}
        items={resolveReligionValue(religion)}
      />
      <WholeMeProfileSectionDetail
        onEdit={onEdit}
        label="Interests & Hobbies"
        items={hobbies.map((n) => fieldLabelFor(n.option_id, 'interests', 'hobbies', n.suboption_id))}
        categoryId="interests"
        stepId="hobbies"
        isViewOnly={isViewOnly}
      />
      <WholeMeProfileSectionDetail
        onEdit={onEdit}
        label="Non-profit & Org Affiliations"
        items={clubs.map((n) => n.value)}
        categoryId="interests"
        stepId="clubs"
        isViewOnly={isViewOnly}
        pillStyles={{ backgroundColor: '#c4c4c4' }}
      />
    </>
  );

  const renderView = () => {
    const createBlock = (label, items) => ({ label, items });

    const preparePillItems = (list) => list.map((n) => ({
      value: fieldLabelFor(n.option_id, n.category_id, n.question_id, n.suboption_id),
      clickContext: {
        infoUrl: fieldInfoUrlFor(n.option_id, n.category_id, n.question_id, n.suboption_id),
      },
    }));

    const blocks = [
      hobbies.length > 0 && createBlock('hobbies', preparePillItems(hobbies)),
      clubs.length > 0
      && createBlock(
        'clubs',
        clubs.map((n) => ({
          value: n.value,
          clickContext: {
            infoUrl: fieldInfoUrlFor(n.option_id, n.category_id, n.question_id, n.suboption_id),
          },
        })),
      ),
    ].filter(Boolean);

    const pillStyles = {
      default: { ...pillOverrideStyles },
      even: { backgroundColor: '#E8E8DC' },
      odd: { backgroundColor: '#C4C4C4' },
    };

    const PillBlocks = blocks.map((block, i) => (
      <PillRenderer
        key={block.label}
        pillStyles={{ ...pillStyles.default, ...(i % 2 === 0 ? pillStyles.even : pillStyles.odd) }}
        containerStyleOverrides={pillContainerOverrideStyles}
        isSelectable={false}
        items={block.items}
      />
    ));

    const QuizBlocks = quiz.map((n) => (
      <WholeMeProfileSectionRow
        key={n.value}
        styleOverrides={{ paddingBottom: '0' }}
        iconSrc={resolveQuizIcon(n.option_id, n.suboption_id, n.value)}
        label={n.value}
        labelStyle={{ color: '#5B5B5B' }}
      />
    ));

    const religionLabel = resolveReligionValue(religion);
    const ReligionBlocks = (
      <WholeMeProfileSectionRow
        key={religionLabel}
        styleOverrides={{ paddingBottom: '0' }}
        label={religionLabel}
        iconSrc={resolveDefaultIcons('identity', religionLabel)}
      />
    );

    if (!blocks.length && !quiz.length && !religion.length) return null;

    return (
      <>
        {!isViewingAdmin && QuizBlocks}
        {!isViewingAdmin && religion.length > 0 && ReligionBlocks}
        {PillBlocks}
      </>
    );
  };

  return (
    <WholeMeProfileSection
      title="Interests"
      editable={!!onEdit}
      titleIcon={<InterestsIcon marginRight={6} height={67} />}
    >
      {onEdit ? renderEdit() : renderView()}
    </WholeMeProfileSection>
  );
};

export default WholeMeProfileInterests;
